// Shape catalog for WannaPublish (Publisher-like Insert > Shapes gallery)
// Every shape is defined in a 100x100 viewBox and stretched to the object box
// (preserveAspectRatio="none" + vector-effect non-scaling-stroke keeps strokes crisp).

const polyStar = (points, inner = 0.45, rot = -90) => {
  const pts = [];
  for (let i = 0; i < points * 2; i++) {
    const r = i % 2 === 0 ? 50 : 50 * inner;
    const a = ((rot + (i * 180) / points) * Math.PI) / 180;
    pts.push(`${(50 + r * Math.cos(a)).toFixed(2)},${(50 + r * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(' ');
};

const regularPolygon = (n, rot = -90) => {
  const pts = [];
  for (let i = 0; i < n; i++) {
    const a = ((rot + (i * 360) / n) * Math.PI) / 180;
    pts.push(`${(50 + 50 * Math.cos(a)).toFixed(2)},${(50 + 50 * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(' ');
};

const explosion = (n, jitter) => {
  const pts = [];
  for (let i = 0; i < n * 2; i++) {
    const base = i % 2 === 0 ? 50 : 32;
    const r = base + (i % 3 === 0 ? jitter : i % 3 === 1 ? -jitter / 2 : jitter / 3);
    const a = ((-90 + (i * 180) / n) * Math.PI) / 180;
    pts.push(`${(50 + r * Math.cos(a)).toFixed(2)},${(50 + r * Math.sin(a)).toFixed(2)}`);
  }
  return pts.join(' ');
};

// geometry: { kind: 'rect'|'ellipse'|'polygon'|'path'|'line'|'multi', ... }
export const SHAPE_GEOMETRY = {
  // ---- Lines ----
  line: { kind: 'line' },
  'line-arrow': { kind: 'line', arrowEnd: true },
  'line-double-arrow': { kind: 'line', arrowEnd: true, arrowStart: true },
  'elbow-connector': { kind: 'path', d: 'M0,10 L50,10 L50,90 L100,90', open: true },
  'elbow-arrow': { kind: 'path', d: 'M0,10 L50,10 L50,90 L100,90', open: true, arrowEnd: true },
  'curved-connector': { kind: 'path', d: 'M0,10 C50,10 50,90 100,90', open: true },
  'curved-arrow-line': { kind: 'path', d: 'M0,10 C50,10 50,90 100,90', open: true, arrowEnd: true },
  curve: { kind: 'path', d: 'M0,80 Q25,0 50,50 T100,20', open: true },
  // ---- Basic ----
  rectangle: { kind: 'rect' },
  'rounded-rect': { kind: 'rect', rounded: true },
  circle: { kind: 'ellipse' },
  ellipse: { kind: 'ellipse' },
  triangle: { kind: 'polygon', points: '50,0 100,100 0,100' },
  'right-triangle': { kind: 'polygon', points: '0,0 100,100 0,100' },
  parallelogram: { kind: 'polygon', points: '25,0 100,0 75,100 0,100' },
  trapezoid: { kind: 'polygon', points: '20,0 80,0 100,100 0,100' },
  diamond: { kind: 'polygon', points: '50,0 100,50 50,100 0,50' },
  pentagon: { kind: 'polygon', points: regularPolygon(5) },
  hexagon: { kind: 'polygon', points: regularPolygon(6, 0) },
  heptagon: { kind: 'polygon', points: regularPolygon(7) },
  octagon: { kind: 'polygon', points: regularPolygon(8, 22.5) },
  decagon: { kind: 'polygon', points: regularPolygon(10) },
  dodecagon: { kind: 'polygon', points: regularPolygon(12, 15) },
  cross: { kind: 'polygon', points: '33,0 67,0 67,33 100,33 100,67 67,67 67,100 33,100 33,67 0,67 0,33 33,33' },
  'l-shape': { kind: 'polygon', points: '0,0 35,0 35,65 100,65 100,100 0,100' },
  chord: { kind: 'path', d: 'M85,15 A50,50 0 1,0 15,85 Z' },
  can: { kind: 'path', d: 'M0,12 C0,-4 100,-4 100,12 L100,88 C100,104 0,104 0,88 Z M0,12 C0,28 100,28 100,12', evenodd: false },
  cube: { kind: 'path', d: 'M0,25 L25,0 L100,0 L100,75 L75,100 L0,100 Z M0,25 L75,25 L75,100 M75,25 L100,0' },
  bevel: { kind: 'path', d: 'M0,0 H100 V100 H0 Z M15,15 H85 V85 H15 Z M0,0 L15,15 M100,0 L85,15 M100,100 L85,85 M0,100 L15,85' },
  'folded-corner': { kind: 'path', d: 'M0,0 H100 V75 L75,100 H0 Z M75,100 L80,80 L100,75' },
  smiley: { kind: 'path', d: 'M50,0 A50,50 0 1,0 50,100 A50,50 0 1,0 50,0 Z M30,35 a5,7 0 1,0 10,0 a5,7 0 1,0 -10,0 M60,35 a5,7 0 1,0 10,0 a5,7 0 1,0 -10,0 M25,62 Q50,88 75,62', open: false },
  donut: { kind: 'path', d: 'M50,0 A50,50 0 1,0 50,100 A50,50 0 1,0 50,0 Z M50,25 A25,25 0 1,1 50,75 A25,25 0 1,1 50,25 Z', evenodd: true },
  'no-symbol': { kind: 'path', d: 'M50,0 A50,50 0 1,0 50,100 A50,50 0 1,0 50,0 Z M50,18 A32,32 0 0,1 77,68 L32,23 A32,32 0 0,1 50,18 Z M23,32 L68,77 A32,32 0 0,1 23,32 Z', evenodd: true },
  'block-arc': { kind: 'path', d: 'M0,100 A50,50 0 0,1 100,100 L75,100 A25,25 0 0,0 25,100 Z' },
  heart: { kind: 'path', d: 'M50,95 C20,65 0,45 0,27 C0,10 15,0 30,0 C40,0 47,5 50,14 C53,5 60,0 70,0 C85,0 100,10 100,27 C100,45 80,65 50,95 Z' },
  lightning: { kind: 'polygon', points: '35,0 70,0 55,38 80,38 30,100 42,55 20,55' },
  sun: { kind: 'path', d: 'M50,25 A25,25 0 1,0 50,75 A25,25 0 1,0 50,25 Z M50,0 L55,15 L45,15 Z M50,100 L45,85 L55,85 Z M0,50 L15,45 L15,55 Z M100,50 L85,55 L85,45 Z M15,15 L28,22 L22,28 Z M85,15 L78,28 L72,22 Z M85,85 L72,78 L78,72 Z M15,85 L22,72 L28,78 Z' },
  moon: { kind: 'path', d: 'M75,0 A50,50 0 1,0 75,100 A40,40 0 1,1 75,0 Z' },
  arc: { kind: 'path', d: 'M0,100 A100,100 0 0,1 100,0', open: true },
  'double-bracket': { kind: 'path', d: 'M20,0 Q0,0 0,20 V80 Q0,100 20,100 M80,0 Q100,0 100,20 V80 Q100,100 80,100', open: true },
  'double-brace': { kind: 'path', d: 'M25,0 Q10,0 10,15 V40 Q10,50 0,50 Q10,50 10,60 V85 Q10,100 25,100 M75,0 Q90,0 90,15 V40 Q90,50 100,50 Q90,50 90,60 V85 Q90,100 75,100', open: true },
  'left-bracket': { kind: 'path', d: 'M100,0 Q0,0 0,20 V80 Q0,100 100,100', open: true },
  'right-bracket': { kind: 'path', d: 'M0,0 Q100,0 100,20 V80 Q100,100 0,100', open: true },
  'left-brace': { kind: 'path', d: 'M100,0 Q50,0 50,15 V40 Q50,50 0,50 Q50,50 50,60 V85 Q50,100 100,100', open: true },
  'right-brace': { kind: 'path', d: 'M0,0 Q50,0 50,15 V40 Q50,50 100,50 Q50,50 50,60 V85 Q50,100 0,100', open: true },
  plaque: { kind: 'path', d: 'M20,0 H80 A20,20 0 0,0 100,20 V80 A20,20 0 0,0 80,100 H20 A20,20 0 0,0 0,80 V20 A20,20 0 0,0 20,0 Z' },
  frame: { kind: 'path', d: 'M0,0 H100 V100 H0 Z M12,12 V88 H88 V12 Z', evenodd: true },
  'half-frame': { kind: 'polygon', points: '0,0 100,0 85,15 15,15 15,85 0,100' },
  tear: { kind: 'path', d: 'M50,100 A50,50 0 1,1 100,50 L100,0 L50,0 A50,50 0 0,0 50,100 Z' },
  pie: { kind: 'path', d: 'M50,50 L50,0 A50,50 0 1,1 14.6,85.4 Z' },
  // ---- Block arrows ----
  'arrow-right': { kind: 'polygon', points: '0,30 60,30 60,0 100,50 60,100 60,70 0,70' },
  'arrow-left': { kind: 'polygon', points: '100,30 40,30 40,0 0,50 40,100 40,70 100,70' },
  'arrow-up': { kind: 'polygon', points: '30,100 30,40 0,40 50,0 100,40 70,40 70,100' },
  'arrow-down': { kind: 'polygon', points: '30,0 30,60 0,60 50,100 100,60 70,60 70,0' },
  'arrow-left-right': { kind: 'polygon', points: '0,50 25,0 25,30 75,30 75,0 100,50 75,100 75,70 25,70 25,100' },
  'arrow-up-down': { kind: 'polygon', points: '50,0 100,25 70,25 70,75 100,75 50,100 0,75 30,75 30,25 0,25' },
  'quad-arrow': { kind: 'polygon', points: '50,0 65,18 57,18 57,43 82,43 82,35 100,50 82,65 82,57 57,57 57,82 65,82 50,100 35,82 43,82 43,57 18,57 18,65 0,50 18,35 18,43 43,43 43,18 35,18' },
  'left-right-up-arrow': { kind: 'polygon', points: '50,0 68,22 58,22 58,42 78,42 78,32 100,50 78,68 78,58 58,58 58,100 42,100 42,58 22,58 22,68 0,50 22,32 22,42 42,42 42,22 32,22' },
  'bent-arrow': { kind: 'path', d: 'M0,100 V50 Q0,20 30,20 H70 V0 L100,30 L70,60 V40 H30 Q20,40 20,50 V100 Z' },
  'u-turn-arrow': { kind: 'path', d: 'M0,100 V40 Q0,0 40,0 H60 Q100,0 100,40 V60 H80 V40 Q80,20 60,20 H40 Q20,20 20,40 V100 Z M70,60 H100 L85,85 Z' },
  'left-up-arrow': { kind: 'polygon', points: '0,25 25,0 50,25 35,25 35,65 75,65 75,50 100,75 75,100 75,85 20,85 15,85 15,25' },
  'bent-up-arrow': { kind: 'polygon', points: '0,80 60,80 60,25 45,25 75,0 100,25 85,25 85,100 0,100' },
  'curved-right-arrow': { kind: 'path', d: 'M0,100 Q0,20 60,20 V0 L100,30 L60,60 V40 Q20,40 20,100 Z' },
  'curved-left-arrow': { kind: 'path', d: 'M100,100 Q100,20 40,20 V0 L0,30 L40,60 V40 Q80,40 80,100 Z' },
  'curved-up-arrow': { kind: 'path', d: 'M100,100 Q20,100 20,40 H0 L30,0 L60,40 H40 Q40,80 100,80 Z' },
  'curved-down-arrow': { kind: 'path', d: 'M100,0 Q20,0 20,60 H0 L30,100 L60,60 H40 Q40,20 100,20 Z' },
  'striped-right-arrow': { kind: 'path', d: 'M0,30 H6 V70 H0 Z M12,30 H20 V70 H12 Z M26,30 H60 V0 L100,50 L60,100 V70 H26 Z' },
  'notched-right-arrow': { kind: 'polygon', points: '0,30 60,30 60,0 100,50 60,100 60,70 0,70 15,50' },
  'pentagon-arrow': { kind: 'polygon', points: '0,0 70,0 100,50 70,100 0,100' },
  chevron: { kind: 'polygon', points: '0,0 70,0 100,50 70,100 0,100 30,50' },
  'right-arrow-callout': { kind: 'polygon', points: '0,0 55,0 55,35 75,35 75,20 100,50 75,80 75,65 55,65 55,100 0,100' },
  'left-arrow-callout': { kind: 'polygon', points: '100,0 45,0 45,35 25,35 25,20 0,50 25,80 25,65 45,65 45,100 100,100' },
  'up-arrow-callout': { kind: 'polygon', points: '0,100 0,45 35,45 35,25 20,25 50,0 80,25 65,25 65,45 100,45 100,100' },
  'down-arrow-callout': { kind: 'polygon', points: '0,0 0,55 35,55 35,75 20,75 50,100 80,75 65,75 65,55 100,55 100,0' },
  'circular-arrow': { kind: 'path', d: 'M50,10 A40,40 0 1,1 12,60 L26,58 A26,26 0 1,0 50,24 Z M50,0 L70,17 L50,34 Z' },
  // ---- Flowchart ----
  'fc-process': { kind: 'rect' },
  'fc-alternate-process': { kind: 'rect', rounded: true },
  'fc-decision': { kind: 'polygon', points: '50,0 100,50 50,100 0,50' },
  'fc-data': { kind: 'polygon', points: '20,0 100,0 80,100 0,100' },
  'fc-predefined-process': { kind: 'path', d: 'M0,0 H100 V100 H0 Z M12,0 V100 M88,0 V100' },
  'fc-internal-storage': { kind: 'path', d: 'M0,0 H100 V100 H0 Z M15,0 V100 M0,15 H100' },
  'fc-document': { kind: 'path', d: 'M0,0 H100 V80 Q75,60 50,80 T0,80 Z' },
  'fc-multidocument': { kind: 'path', d: 'M0,20 H80 V90 Q60,75 40,90 T0,90 Z M10,10 H90 V80 M20,0 H100 V70' },
  'fc-terminator': { kind: 'path', d: 'M25,0 H75 A25,50 0 0,1 75,100 H25 A25,50 0 0,1 25,0 Z' },
  'fc-preparation': { kind: 'polygon', points: '20,0 80,0 100,50 80,100 20,100 0,50' },
  'fc-manual-input': { kind: 'polygon', points: '0,30 100,0 100,100 0,100' },
  'fc-manual-operation': { kind: 'polygon', points: '0,0 100,0 80,100 20,100' },
  'fc-connector': { kind: 'ellipse' },
  'fc-off-page-connector': { kind: 'polygon', points: '0,0 100,0 100,70 50,100 0,70' },
  'fc-card': { kind: 'polygon', points: '20,0 100,0 100,100 0,100 0,20' },
  'fc-punched-tape': { kind: 'path', d: 'M0,10 Q25,30 50,10 T100,10 V90 Q75,70 50,90 T0,90 Z' },
  'fc-summing-junction': { kind: 'path', d: 'M50,0 A50,50 0 1,0 50,100 A50,50 0 1,0 50,0 Z M15,15 L85,85 M85,15 L15,85' },
  'fc-or': { kind: 'path', d: 'M50,0 A50,50 0 1,0 50,100 A50,50 0 1,0 50,0 Z M50,0 V100 M0,50 H100' },
  'fc-collate': { kind: 'polygon', points: '0,0 100,0 0,100 100,100' },
  'fc-sort': { kind: 'path', d: 'M50,0 L100,50 L50,100 L0,50 Z M0,50 H100' },
  'fc-extract': { kind: 'polygon', points: '50,0 100,100 0,100' },
  'fc-merge': { kind: 'polygon', points: '0,0 100,0 50,100' },
  'fc-stored-data': { kind: 'path', d: 'M20,0 H100 A20,50 0 0,0 100,100 H20 A20,50 0 0,1 20,0 Z' },
  'fc-delay': { kind: 'path', d: 'M0,0 H50 A50,50 0 0,1 50,100 H0 Z' },
  'fc-sequential-storage': { kind: 'path', d: 'M50,0 A50,50 0 1,0 50,100 H100 V90 H85 A50,50 0 0,0 50,0 Z' },
  'fc-magnetic-disk': { kind: 'path', d: 'M0,15 C0,-5 100,-5 100,15 V85 C100,105 0,105 0,85 Z M0,15 C0,35 100,35 100,15' },
  'fc-direct-access-storage': { kind: 'path', d: 'M15,0 H85 C105,0 105,100 85,100 H15 C-5,100 -5,0 15,0 Z M85,0 C65,0 65,100 85,100' },
  'fc-display': { kind: 'path', d: 'M0,50 L20,0 H80 A20,50 0 0,1 80,100 H20 Z' },
  // ---- Callouts ----
  'callout-rect': { kind: 'polygon', points: '0,0 100,0 100,75 40,75 20,100 25,75 0,75' },
  'callout-rounded': { kind: 'path', d: 'M10,0 H90 Q100,0 100,10 V65 Q100,75 90,75 H40 L20,100 L25,75 H10 Q0,75 0,65 V10 Q0,0 10,0 Z' },
  'callout-oval': { kind: 'path', d: 'M50,0 A50,37 0 1,1 35,72 L20,100 L28,71 A50,37 0 0,1 50,0 Z' },
  'callout-cloud': { kind: 'path', d: 'M25,70 Q5,70 8,52 Q0,38 15,32 Q12,12 32,15 Q40,0 58,8 Q75,0 82,15 Q100,15 96,35 Q108,50 92,60 Q95,78 75,75 Q60,88 45,75 Q35,82 25,70 Z M22,80 a5,4 0 1,0 10,0 a5,4 0 1,0 -10,0 M12,92 a4,3 0 1,0 8,0 a4,3 0 1,0 -8,0' },
  'callout-thought': { kind: 'path', d: 'M50,0 A50,35 0 1,1 50,70 A50,35 0 1,1 50,0 Z M30,80 a6,5 0 1,0 12,0 a6,5 0 1,0 -12,0 M18,93 a4,3 0 1,0 8,0 a4,3 0 1,0 -8,0' },
  'callout-line-1': { kind: 'path', d: 'M30,0 H100 V60 H30 Z M30,30 L0,100', open: true },
  'callout-line-2': { kind: 'path', d: 'M30,0 H100 V60 H30 Z M30,30 L15,30 L0,100', open: true },
  'callout-line-3': { kind: 'path', d: 'M30,0 H100 V60 H30 Z M30,30 L20,30 L10,70 L0,100', open: true },
  'callout-line-accent': { kind: 'path', d: 'M30,0 H100 V60 H30 Z M30,0 V60 M30,30 L0,100', open: true },
  // ---- Stars and banners ----
  'explosion-1': { kind: 'polygon', points: explosion(9, 10) },
  'explosion-2': { kind: 'polygon', points: explosion(12, 14) },
  'star-4': { kind: 'polygon', points: polyStar(4, 0.4) },
  star: { kind: 'polygon', points: polyStar(5, 0.38) },
  'star-5': { kind: 'polygon', points: polyStar(5, 0.38) },
  'star-6': { kind: 'polygon', points: polyStar(6, 0.55) },
  'star-7': { kind: 'polygon', points: polyStar(7, 0.55) },
  'star-8': { kind: 'polygon', points: polyStar(8, 0.65) },
  'star-10': { kind: 'polygon', points: polyStar(10, 0.7) },
  'star-12': { kind: 'polygon', points: polyStar(12, 0.75) },
  'star-16': { kind: 'polygon', points: polyStar(16, 0.8) },
  'star-24': { kind: 'polygon', points: polyStar(24, 0.85) },
  'star-32': { kind: 'polygon', points: polyStar(32, 0.88) },
  'ribbon-up': { kind: 'path', d: 'M0,15 H25 V0 H75 V15 H100 L88,35 L100,55 H75 V70 H25 V55 H0 L12,35 Z M25,15 V55 M75,15 V55' },
  'ribbon-down': { kind: 'path', d: 'M0,45 L12,65 L0,85 H25 V100 H75 V85 H100 L88,65 L100,45 H75 V30 H25 V45 Z M25,45 V85 M75,45 V85' },
  'curved-ribbon-up': { kind: 'path', d: 'M0,20 Q50,45 100,20 V70 Q50,95 0,70 Z M0,20 V0 H20 V26 M100,20 V0 H80 V26' },
  'curved-ribbon-down': { kind: 'path', d: 'M0,30 Q50,5 100,30 V80 Q50,55 0,80 Z M0,80 V100 H20 V74 M100,80 V100 H80 V74' },
  'vertical-scroll': { kind: 'path', d: 'M15,10 H90 Q100,10 100,20 V90 Q100,100 90,100 H15 Q5,100 5,90 V20 Q5,10 15,10 Z M15,10 Q5,10 5,20 Q5,30 15,30 Q22,30 22,20 Q22,10 15,10 M85,100 Q95,100 95,90 Q95,80 85,80' },
  'horizontal-scroll': { kind: 'path', d: 'M10,15 H90 Q100,15 100,25 V85 Q100,95 90,95 H10 Q0,95 0,85 V25 Q0,15 10,15 Z M10,15 Q0,15 0,25 Q0,35 10,35 Q20,35 20,25 Q20,15 10,15 M90,95 Q100,95 100,85 Q100,75 90,75' },
  wave: { kind: 'path', d: 'M0,20 Q25,0 50,20 T100,20 V80 Q75,100 50,80 T0,80 Z' },
  'double-wave': { kind: 'path', d: 'M0,20 Q12,5 25,20 T50,20 T75,20 T100,20 V80 Q88,95 75,80 T50,80 T25,80 T0,80 Z' },
  // legacy ids from the old app
  'badge-circle': { kind: 'ellipse' },
  'badge-star': { kind: 'polygon', points: polyStar(12, 0.8) },
  'badge-ribbon': { kind: 'path', d: 'M0,15 H25 V0 H75 V15 H100 L88,35 L100,55 H75 V70 H25 V55 H0 L12,35 Z' },
  'badge-seal': { kind: 'polygon', points: polyStar(16, 0.86) },
  'frame-simple': { kind: 'path', d: 'M0,0 H100 V100 H0 Z M8,8 V92 H92 V8 Z', evenodd: true },
  'frame-double': { kind: 'path', d: 'M0,0 H100 V100 H0 Z M5,5 V95 H95 V5 Z M10,10 H90 V90 H10 Z M14,14 V86 H86 V14 Z', evenodd: true },
  'line-chevron': { kind: 'polygon', points: '0,0 70,0 100,50 70,100 0,100 30,50' },
};

export const SHAPE_CATEGORIES = [
  {
    id: 'lines',
    label: 'Lines',
    shapes: [
      ['line', 'Line'], ['line-arrow', 'Line Arrow'], ['line-double-arrow', 'Line Arrow: Double'],
      ['elbow-connector', 'Connector: Elbow'], ['elbow-arrow', 'Connector: Elbow Arrow'],
      ['curved-connector', 'Connector: Curved'], ['curved-arrow-line', 'Connector: Curved Arrow'], ['curve', 'Curve'],
    ],
  },
  {
    id: 'basic',
    label: 'Basic Shapes',
    shapes: [
      ['textbox', 'Text Box'], ['rectangle', 'Rectangle'], ['rounded-rect', 'Rectangle: Rounded Corners'], ['parallelogram', 'Parallelogram'],
      ['trapezoid', 'Trapezoid'], ['diamond', 'Diamond'], ['octagon', 'Octagon'], ['triangle', 'Isosceles Triangle'],
      ['right-triangle', 'Right Triangle'], ['circle', 'Oval'], ['hexagon', 'Hexagon'], ['cross', 'Cross'],
      ['pentagon', 'Pentagon'], ['heptagon', 'Heptagon'], ['decagon', 'Decagon'], ['dodecagon', 'Dodecagon'],
      ['can', 'Cylinder'], ['cube', 'Cube'], ['bevel', 'Bevel'], ['folded-corner', 'Folded Corner'],
      ['smiley', 'Smiley Face'], ['donut', 'Donut'], ['no-symbol', 'No Symbol'], ['block-arc', 'Block Arc'],
      ['heart', 'Heart'], ['lightning', 'Lightning Bolt'], ['sun', 'Sun'], ['moon', 'Moon'],
      ['arc', 'Arc'], ['double-bracket', 'Double Bracket'], ['double-brace', 'Double Brace'], ['plaque', 'Plaque'],
      ['left-bracket', 'Left Bracket'], ['right-bracket', 'Right Bracket'], ['left-brace', 'Left Brace'], ['right-brace', 'Right Brace'],
      ['frame', 'Frame'], ['half-frame', 'Half Frame'], ['l-shape', 'L-Shape'], ['chord', 'Chord'], ['tear', 'Teardrop'], ['pie', 'Pie'],
    ],
  },
  {
    id: 'block-arrows',
    label: 'Block Arrows',
    shapes: [
      ['arrow-right', 'Arrow: Right'], ['arrow-left', 'Arrow: Left'], ['arrow-up', 'Arrow: Up'], ['arrow-down', 'Arrow: Down'],
      ['arrow-left-right', 'Arrow: Left-Right'], ['arrow-up-down', 'Arrow: Up-Down'], ['quad-arrow', 'Arrow: Quad'], ['left-right-up-arrow', 'Arrow: Left-Right-Up'],
      ['bent-arrow', 'Arrow: Bent'], ['u-turn-arrow', 'Arrow: U-Turn'], ['left-up-arrow', 'Arrow: Left-Up'], ['bent-up-arrow', 'Arrow: Bent-Up'],
      ['curved-right-arrow', 'Arrow: Curved Right'], ['curved-left-arrow', 'Arrow: Curved Left'], ['curved-up-arrow', 'Arrow: Curved Up'], ['curved-down-arrow', 'Arrow: Curved Down'],
      ['striped-right-arrow', 'Arrow: Striped Right'], ['notched-right-arrow', 'Arrow: Notched Right'], ['pentagon-arrow', 'Arrow: Pentagon'], ['chevron', 'Arrow: Chevron'],
      ['right-arrow-callout', 'Arrow: Right Callout'], ['left-arrow-callout', 'Arrow: Left Callout'], ['up-arrow-callout', 'Arrow: Up Callout'], ['down-arrow-callout', 'Arrow: Down Callout'],
      ['circular-arrow', 'Arrow: Circular'],
    ],
  },
  {
    id: 'flowchart',
    label: 'Flowchart',
    shapes: [
      ['fc-process', 'Flowchart: Process'], ['fc-alternate-process', 'Flowchart: Alternate Process'], ['fc-decision', 'Flowchart: Decision'], ['fc-data', 'Flowchart: Data'],
      ['fc-predefined-process', 'Flowchart: Predefined Process'], ['fc-internal-storage', 'Flowchart: Internal Storage'], ['fc-document', 'Flowchart: Document'], ['fc-multidocument', 'Flowchart: Multidocument'],
      ['fc-terminator', 'Flowchart: Terminator'], ['fc-preparation', 'Flowchart: Preparation'], ['fc-manual-input', 'Flowchart: Manual Input'], ['fc-manual-operation', 'Flowchart: Manual Operation'],
      ['fc-connector', 'Flowchart: Connector'], ['fc-off-page-connector', 'Flowchart: Off-page Connector'], ['fc-card', 'Flowchart: Card'], ['fc-punched-tape', 'Flowchart: Punched Tape'],
      ['fc-summing-junction', 'Flowchart: Summing Junction'], ['fc-or', 'Flowchart: Or'], ['fc-collate', 'Flowchart: Collate'], ['fc-sort', 'Flowchart: Sort'],
      ['fc-extract', 'Flowchart: Extract'], ['fc-merge', 'Flowchart: Merge'], ['fc-stored-data', 'Flowchart: Stored Data'], ['fc-delay', 'Flowchart: Delay'],
      ['fc-sequential-storage', 'Flowchart: Sequential Access Storage'], ['fc-magnetic-disk', 'Flowchart: Magnetic Disk'], ['fc-direct-access-storage', 'Flowchart: Direct Access Storage'], ['fc-display', 'Flowchart: Display'],
    ],
  },
  {
    id: 'callouts',
    label: 'Callouts',
    shapes: [
      ['callout-rect', 'Speech Bubble: Rectangle'], ['callout-rounded', 'Speech Bubble: Rounded Rectangle'], ['callout-oval', 'Speech Bubble: Oval'], ['callout-cloud', 'Thought Bubble: Cloud'],
      ['callout-thought', 'Thought Bubble: Oval'], ['callout-line-1', 'Line Callout 1'], ['callout-line-2', 'Line Callout 2'], ['callout-line-3', 'Line Callout 3'], ['callout-line-accent', 'Line Callout: Accent Bar'],
    ],
  },
  {
    id: 'stars',
    label: 'Stars and Banners',
    shapes: [
      ['explosion-1', 'Explosion: 8 Points'], ['explosion-2', 'Explosion: 14 Points'], ['star-4', 'Star: 4 Points'], ['star-5', 'Star: 5 Points'],
      ['star-6', 'Star: 6 Points'], ['star-7', 'Star: 7 Points'], ['star-8', 'Star: 8 Points'], ['star-10', 'Star: 10 Points'],
      ['star-12', 'Star: 12 Points'], ['star-16', 'Star: 16 Points'], ['star-24', 'Star: 24 Points'], ['star-32', 'Star: 32 Points'],
      ['ribbon-up', 'Ribbon: Tilted Up'], ['ribbon-down', 'Ribbon: Tilted Down'], ['curved-ribbon-up', 'Ribbon: Curved and Tilted Up'], ['curved-ribbon-down', 'Ribbon: Curved and Tilted Down'],
      ['vertical-scroll', 'Scroll: Vertical'], ['horizontal-scroll', 'Scroll: Horizontal'], ['wave', 'Wave'], ['double-wave', 'Double Wave'],
    ],
  },
].map((c) => ({ ...c, shapes: c.shapes.map(([id, label]) => ({ id, label })) }));

export const SHAPE_LABELS = Object.fromEntries(SHAPE_CATEGORIES.flatMap((c) => c.shapes.map((s) => [s.id, s.label])));

export const getShapeLabel = (id) => SHAPE_LABELS[id] || (id || 'Shape').replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());

export const isLineShape = (id) => {
  const g = SHAPE_GEOMETRY[id];
  return !!g && (g.kind === 'line' || g.open);
};

export const RECENT_SHAPES_KEY = 'wp.recentShapes';
export const getRecentShapes = () => {
  try {
    return JSON.parse(localStorage.getItem(RECENT_SHAPES_KEY) || '[]');
  } catch {
    return [];
  }
};
export const pushRecentShape = (id) => {
  const list = [id, ...getRecentShapes().filter((s) => s !== id)].slice(0, 12);
  localStorage.setItem(RECENT_SHAPES_KEY, JSON.stringify(list));
  return list;
};
